import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { forwardRef } from 'preact/compat';
import { BRAND_CLASSES } from '@/constants/theme';
import { getLabelClasses } from './formLabelUtils';
/**
 * Common DateTimePicker component for date and time selection
 * Integrates with React Hook Form via forwardRef
 * Uses HTML5 datetime-local input type for native browser support
 *
 * @example
 * ```tsx
 * // Basic usage with React Hook Form
 * <DateTimePicker
 *   label="Start Time"
 *   required
 *   error={errors.startTime?.message}
 *   {...register('startTime', { required: 'Start time is required' })}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With validation
 * <DateTimePicker
 *   label="End Time"
 *   required
 *   error={errors.endTime?.message}
 *   helperText="Must be after start time"
 *   {...register('endTime', {
 *     required: 'End time is required',
 *     validate: (value) => {
 *       const start = getValues('startTime')
 *       if (start && value && new Date(value) <= new Date(start)) {
 *         return 'End time must be after start time'
 *       }
 *       return true
 *     }
 *   })}
 * />
 * ```
 */
export const DateTimePicker = forwardRef(({ label, required, error, helperText, containerClassName, className, labelSize, ...props }, ref) => {
    const inputClasses = `
            w-full px-3 py-2 border rounded-lg
            ${BRAND_CLASSES.INPUT.NORMAL}
            [color-scheme:light] dark:[color-scheme:dark]
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error
        ? BRAND_CLASSES.INPUT.ERROR
        : BRAND_CLASSES.FOCUS.PRIMARY}
            ${className || ''}
        `.trim().replace(/\s+/g, ' ');
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { className: getLabelClasses(labelSize), children: [label, required && _jsx("span", { className: BRAND_CLASSES.INPUT.REQUIRED, children: " *" })] })), _jsx("input", { ref: ref, type: "datetime-local", className: inputClasses, ...props }), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), !error && helperText && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
});
DateTimePicker.displayName = 'DateTimePicker';
